import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input
} from '@angular/core';
import { Store } from '@ngrx/store';
import { MojoFeatureExtensionsService } from 'mojo-feature-extensions';
import { Observable, combineLatest, EMPTY } from 'rxjs';
import { map } from 'rxjs/operators';

import { AppFeatureExtensions } from '@app/app-feature-extensions.config';
import { getAssociationLabel } from '@collections/association-label';
import {
  getAssociationRejectedConfigurations,
  isAssociationRejected,
} from '@modules/assigns/store';
import {
  noteRejectedModalShow,
} from '@shared/modal';
import {
  Association,
  AssociationLabel,
  AssociationStatusConfiguration,
} from '@shared/models';

@Component({
  selector: 'mojo-candidate-reject-dropdown',
  templateUrl: './candidate-reject-dropdown.component.html',
  styleUrls: ['./candidate-reject-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CandidateRejectDropdownComponent {
  rejectedStates$: Observable<AssociationStatusConfiguration[]>;
  showMenu$: Observable<boolean>;

  private _association: Association;

  @Input()
  set association(association: Association) {
    if (!!association) {
      this.rejectedStates$ = this.store.select(getAssociationRejectedConfigurations,
        { jobId: association.reqId, configurationStateId: association.status }
      );
      this.showMenu$ = this.shouldDisplayMenuOnDynamicFlow$(association);
      this._association = association;

    } else {
      this.rejectedStates$ = EMPTY;
      this.showMenu$ = EMPTY;
      this._association = null;
    }
  }

  get association(): Association {
    return this._association || {} as Association;
  }

  constructor(
    private store: Store<{}>,
    private cdf: ChangeDetectorRef,
    private featureExtensionsService: MojoFeatureExtensionsService,
  ) { }

  private shouldDisplayMenuOnDynamicFlow$(
    { candidateId, reqId: jobId, status: phaseId, subStatus: stateId}: Association
  ) {
    return combineLatest([
      this.featureExtensionsService.hasFeatureExtension(AppFeatureExtensions.ASSOCIATION_DIRECT_APPLICANT),
      this.store.select(getAssociationLabel, { candidateId, jobId }),
      this.store.select(isAssociationRejected, {
        jobId,
        phaseId,
        stateId
      }),
      this.store.select(getAssociationRejectedConfigurations,
        { jobId, configurationStateId: phaseId }
      ),
    ])
      .pipe(
        map(([onlyDirectApplicants, label, isRejected, rejectedStates]) =>
          onlyDirectApplicants
            ? label === AssociationLabel.DIRECT && this.canBeRejected(isRejected, rejectedStates)
            : this.canBeRejected(isRejected, rejectedStates)));
  }

  private canBeRejected(isRejected: boolean, rejectedStates: AssociationStatusConfiguration[]) {
    return !isRejected && !!rejectedStates && rejectedStates.length > 0;
  }

  onMenuClosed() {
    this.cdf.detectChanges();
  }

  rejectStatus(statusLabel: string, statusId?: string) {
    this.rejectWithNote(statusId);
  }

  private rejectWithNote(status: string) {
    this.store.dispatch(
      noteRejectedModalShow({
        payload: {
          jobId: this.association.reqId,
          candidateId: this.association.candidateId,
          subStatus: status,
        },
      }),
    );
  }
}
